// src/pages/Appli/AppliAdmin/VisitorCenter/components/ConversationsPanel.jsx

import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  FiMessageCircle,
  FiSend,
} from "react-icons/fi";

import {
  CONVERSATION_STATUS_OPTIONS,
} from "../data/visitorCenterData";

import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  SectionCard,
} from "../ui/VisitorCenterUi";

import {
  formatDateTime,
  formatLocation,
} from "../utils/visitorFormatters";

function ConversationMessage({ message }) {
  const admin = message.sender_type === "admin";
  const system = message.sender_type === "system";

  if (system) {
    return (
      <div className="my-3 text-center text-xs text-slate-400">
        {message.message}
      </div>
    );
  }

  return (
    <div
      className={`flex ${
        admin ? "justify-end" : "justify-start"
      }`}
    >
      <div
        className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm ${
          admin
            ? "rounded-br-md bg-[#123B57] text-white"
            : "rounded-bl-md bg-slate-100 text-slate-700"
        }`}
      >
        <div className="whitespace-pre-wrap">
          {message.message}
        </div>

        <div
          className={`mt-2 text-[11px] ${
            admin
              ? "text-white/60"
              : "text-slate-400"
          }`}
        >
          {formatDateTime(message.created_at)}
        </div>
      </div>
    </div>
  );
}

export default function ConversationsPanel({
  conversations,
  selectedConversationId,
  selectedConversation,

  status,
  onlyUnread,

  page,
  totalPages,

  loading,
  detailsLoading,
  sending,
  errorMessage,

  onStatusChange,
  onOnlyUnreadChange,
  onPageChange,
  onSelectConversation,
  onSendMessage,
  onConversationStatusChange,
}) {
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef(null);

  const messages =
    selectedConversation?.messages || [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages.length]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!message.trim()) return;

    await onSendMessage(message);
    setMessage("");
  };

  return (
    <div className="grid gap-6 2xl:grid-cols-[390px_minmax(0,1fr)]">
      <SectionCard
        title="Boîte de réception"
        subtitle="Conversations initiées depuis le site"
      >
        <div className="space-y-3 border-b border-slate-100 p-4">
          <select
            value={status}
            onChange={(event) =>
              onStatusChange(event.target.value)
            }
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#2EA7E0]"
          >
            {CONVERSATION_STATUS_OPTIONS.map(
              (option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ),
            )}
          </select>

          <label className="flex items-center gap-3 rounded-xl bg-[#F8FBFD] px-3 py-2.5 text-sm font-semibold text-slate-600">
            <input
              type="checkbox"
              checked={onlyUnread}
              onChange={(event) =>
                onOnlyUnreadChange(
                  event.target.checked,
                )
              }
            />

            Seulement les messages non lus
          </label>
        </div>

        <ErrorState message={errorMessage} />

        {loading ? (
          <LoadingState label="Chargement des conversations..." />
        ) : conversations.length ? (
          <div className="max-h-[660px] space-y-2 overflow-y-auto p-3">
            {conversations.map((conversation) => {
              const active =
                conversation.id ===
                selectedConversationId;

              return (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() =>
                    onSelectConversation(
                      conversation.id,
                    )
                  }
                  className={`w-full rounded-2xl border p-4 text-left transition ${
                    active
                      ? "border-[#2EA7E0] bg-[#EEF7FC]"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-extrabold text-[#123B57]">
                        {conversation.visitor_name ||
                          `Conversation #${conversation.id}`}
                      </div>

                      <div className="mt-1 truncate text-xs text-slate-500">
                        {conversation.subject ||
                          conversation.session
                            ?.current_page ||
                          "Demande depuis le site"}
                      </div>
                    </div>

                    {conversation.unread_admin_count >
                      0 && (
                      <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                        {
                          conversation.unread_admin_count
                        }
                      </span>
                    )}
                  </div>

                  <div className="mt-3 text-xs text-slate-400">
                    {formatDateTime(
                      conversation.last_message_at ||
                        conversation.created_at,
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={FiMessageCircle}
            title="Aucune conversation"
            description="Les messages envoyés depuis le widget apparaîtront ici."
          />
        )}

        <Pagination
          page={page}
          totalPages={totalPages}
          onChange={onPageChange}
        />
      </SectionCard>

      <SectionCard
        title={
          selectedConversation?.visitor_name ||
          "Conversation"
        }
        subtitle={
          selectedConversation?.session
            ? `${formatLocation(
                selectedConversation.session,
              )} · ${
                selectedConversation.session
                  .current_page || "Page inconnue"
              }`
            : "Sélectionnez une conversation"
        }
        action={
          selectedConversation ? (
            <select
              value={selectedConversation.status}
              onChange={(event) =>
                onConversationStatusChange(
                  event.target.value,
                )
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold outline-none"
            >
              {CONVERSATION_STATUS_OPTIONS.filter(
                (option) => option.value,
              ).map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          ) : null
        }
      >
        {detailsLoading ? (
          <LoadingState label="Chargement de la conversation..." />
        ) : selectedConversation ? (
          <div className="flex min-h-[650px] flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto p-5 lg:p-6">
              {messages.length ? (
                messages.map((item) => (
                  <ConversationMessage
                    key={item.id}
                    message={item}
                  />
                ))
              ) : (
                <EmptyState
                  title="Aucun message"
                  description="La conversation est ouverte mais aucun message n’a encore été envoyé."
                />
              )}

              <div ref={messagesEndRef} />
            </div>

            <form
              onSubmit={handleSubmit}
              className="border-t border-slate-100 p-4 lg:p-5"
            >
              <div className="flex items-end gap-3">
                <textarea
                  value={message}
                  onChange={(event) =>
                    setMessage(event.target.value)
                  }
                  rows={3}
                  placeholder="Écrire une réponse..."
                  disabled={
                    selectedConversation.status ===
                    "closed"
                  }
                  className="min-h-[86px] flex-1 resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#2EA7E0] disabled:bg-slate-100"
                />

                <button
                  type="submit"
                  disabled={
                    sending ||
                    !message.trim() ||
                    selectedConversation.status ===
                      "closed"
                  }
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#123B57] text-white transition hover:bg-[#1C567F] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FiSend />
                </button>
              </div>
            </form>
          </div>
        ) : (
          <EmptyState
            title="Sélectionnez une conversation"
            description="Le parcours du visiteur et les messages apparaîtront ici."
          />
        )}
      </SectionCard>
    </div>
  );
}